// ABOUTME: The talent's interview as pro-core returns it, typed by hand from InterviewHttp and the request records of
// ABOUTME: the guest (/guest/interviews/{refId}) and the signed-in (/talent/interviews/{refId}) routes, which share one shape.

/** Where an interview happens (InterviewKind). Only VIDEO has a room to join. */
export enum InterviewKind {
  VIDEO = 'VIDEO',
  OWN_LINK = 'OWN_LINK',
  PHONE = 'PHONE',
  ON_SITE = 'ON_SITE',
  TRIAL_SHIFT = 'TRIAL_SHIFT'
}

/**
 * The six states (InterviewStatus). OPEN waits for a time, SCHEDULED has a booked one, NO_SHOW may be booked again or
 * cancelled; COMPLETED, CANCELLED and EXPIRED are terminal.
 */
export enum InterviewStatus {
  OPEN = 'OPEN',
  SCHEDULED = 'SCHEDULED',
  COMPLETED = 'COMPLETED',
  NO_SHOW = 'NO_SHOW',
  CANCELLED = 'CANCELLED',
  EXPIRED = 'EXPIRED'
}

/** HELD after a pick until `heldUntil`, CONFIRMED once booked. The card only ever carries the active one. */
export enum InterviewAppointmentStatus {
  HELD = 'HELD',
  CONFIRMED = 'CONFIRMED',
  CANCELLED = 'CANCELLED',
  REPLACED = 'REPLACED'
}

/**
 * The pick-list behind a move or a cancel (InterviewChangeReason). CANDIDATE_LEFT_STEP is the system's alone and the
 * backend refuses it from a person, so it is not listed.
 */
export enum InterviewChangeReason {
  SCHEDULE_CONFLICT = 'SCHEDULE_CONFLICT',
  ILLNESS = 'ILLNESS',
  FOUND_OTHER_JOB = 'FOUND_OTHER_JOB',
  NO_LONGER_INTERESTED = 'NO_LONGER_INTERESTED',
  POSITION_FILLED = 'POSITION_FILLED',
  NO_TIME_FOUND = 'NO_TIME_FOUND',
  OTHER = 'OTHER'
}

/** Who a guest session belongs to (InterviewParticipantRole). Only TALENT gets answers from the talent routes. */
export enum InterviewParticipantRole {
  HOST = 'HOST',
  TALENT = 'TALENT',
  ATTENDEE = 'ATTENDEE'
}

/** A stretch of time, [startsAt, endsAt), both ISO-8601 in UTC. */
export interface InterviewTime {
  startsAt: string;
  endsAt: string;
  /** PJL: an offered time against the talent's week; absent on proposals and before pro-core sends it. */
  fit?: InterviewTimeFit;
}

/** In the talent's preferred times, in their available times, or outside both. */
export type InterviewTimeFit = 'PREFERRED' | 'AVAILABLE' | 'OUTSIDE';

export interface InterviewAppointment extends InterviewTime {
  refId: string;
  status: InterviewAppointmentStatus;
  /** While HELD, when the hold lapses; null once CONFIRMED. */
  heldUntil: string | null;
  /**
   * When the talent answered "Still on?" with "Ich komme" for this booked time (pro-core #1972); null until then and
   * while HELD. Per appointment: a move books a new time, which asks again. Absent on an older image.
   */
  attendanceConfirmedAt?: string | null;
}

/**
 * When a talent may propose times (#1907): starts in [earliest, latest], each at least durationMinutes long, at most
 * maxProposals rows. Absent on an image older than #1907, and then the form asks nothing up front.
 */
export interface ProposalWindow {
  earliest: string;
  latest: string;
  durationMinutes: number;
  maxProposals: number;
}

/** The card (InterviewHttp): GET of the interview and the answer of every command, so the page redraws from it. */
export interface InterviewCardData {
  refId: string;
  round: number;
  kind: InterviewKind;
  durationMinutes: number;
  status: InterviewStatus;
  /** The IANA zone the times are shown in. */
  timeZone: string;
  jobOpeningTitle: string;
  companyName: string;
  /** The held or booked time, null while none is. */
  appointment: InterviewAppointment | null;
  /** While OPEN, the times the talent may hold now, earliest first; empty otherwise. */
  availableTimes: InterviewTime[];
  /** While OPEN, the talent's proposals the host has not answered yet; empty otherwise. */
  proposedTimes: InterviewTime[];
  /** While OPEN, the window a proposal must fit (#1907); absent or null on an older image. */
  proposalWindow?: ProposalWindow | null;
  /** SCHEDULED, outside noMovesWithinMinutes and under maxReschedules. */
  canMove: boolean;
  /** For a VIDEO interview, when the join pass is first handed out; null otherwise. */
  joinOpensAt: string | null;
}

/** InterviewJoinPassHttp: open joinUrl at once, ask again after expiresAt. */
export interface InterviewJoinPass {
  joinUrl: string;
  expiresAt: string;
}

/** InterviewGuestSessionHttp: the answer of POST /guest/interviews/redeem. */
export interface InterviewGuestSession {
  sessionToken: string;
  expiresAt: string;
  interviewRefId: string;
  participantRefId: string;
  role: InterviewParticipantRole;
}

export interface HoldInterviewTimeRequest {
  startsAt: string;
}

export interface ProposeInterviewTimesRequest {
  ranges: InterviewTime[];
}

export interface ChangeInterviewRequest {
  reason: InterviewChangeReason;
  /** At most 1000 characters. */
  note?: string;
}

/**
 * The one interface the card talks to, so the same component works behind the email link (guest session) and in the
 * tracker (Auth0). `scope` keeps the two caches apart.
 */
export interface InterviewApi {
  scope: 'guest' | 'talent';
  /**
   * The credential the cached card belongs to, so another sign-in on this device never sees the last one's card: the
   * Auth0 access token for the signed-in variant (as the applications queries key on it). Null for the guest variant,
   * whose cache is already per link: its key is the interview ref the guest session was redeemed for.
   */
  identity: string | null;
  /** False while the credential is not there yet (the Auth0 token is still loading). */
  isReady: boolean;
  getCard: (refId: string) => Promise<InterviewCardData>;
  hold: (
    refId: string,
    body: HoldInterviewTimeRequest
  ) => Promise<InterviewCardData>;
  confirmHold: (refId: string) => Promise<InterviewCardData>;
  propose: (
    refId: string,
    body: ProposeInterviewTimesRequest
  ) => Promise<InterviewCardData>;
  confirmAttendance: (refId: string) => Promise<InterviewCardData>;
  move: (
    refId: string,
    body: ChangeInterviewRequest
  ) => Promise<InterviewCardData>;
  cancel: (
    refId: string,
    body: ChangeInterviewRequest
  ) => Promise<InterviewCardData>;
  rebook: (refId: string) => Promise<InterviewCardData>;
  join: (refId: string) => Promise<InterviewJoinPass>;
}
